// src/hooks/useAuthorization.ts
import { useCallback, useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";
import type { LocationConfig } from "@/data/locations";
import { isGazelleEmail } from "@/lib/gazelle-staff";

// authorized user manager groupid
const AUTHORIZED_USER_MANAGER_GROUP_ID = "8cba404f-a3d3-4676-9923-1484786936ce";

// localStorage key prefix for per-user location
const LOCATION_STORAGE_PREFIX = "io-location:";

type AuthState = {
  isAuthorized: boolean;
  isCanadian: boolean;
  isGazelle: boolean;
  isLoaded: boolean;
};

type LocationSelectionReason =
  | "current_state"
  | "stored_selection"
  | "dynamic_home_location"
  | "single_allowed_location"
  | "multiple_dynamic_locations"
  | "multiple_allowed_locations"
  | "no_allowed_locations";

type LocationSelection = {
  location: LocationConfig | null;
  reason: LocationSelectionReason;
};

type CurrentAuthorizationResponse = {
  locations?: LocationConfig[];
  auth?: {
    isAuthorizedUserManager?: boolean;
  };
};

type CurrentAuthorizationResult = {
  locations: LocationConfig[];
  isAuthorizedUserManager: boolean;
};

// In-flight dedupe only. No TTL cache.
// This prevents duplicate simultaneous Graph lookups without making group/location data stale.
const inFlightByUser = new Map<string, Promise<CurrentAuthorizationResult>>();

function makeRequestId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }

  return `req_${Date.now()}_${Math.random().toString(36).slice(2)}`;
}

function shouldLogAuthDebug() {
  return process.env.NODE_ENV !== "production" || process.env.NEXT_PUBLIC_DEBUG_AUTH === "true";
}

function getLocationStorageKey(userEmail: string) {
  return `${LOCATION_STORAGE_PREFIX}${userEmail}`;
}

function getStoredLocationId(userEmail: string | null) {
  if (typeof window === "undefined" || !userEmail) return null;

  return window.localStorage.getItem(getLocationStorageKey(userEmail));
}

function locationMatchesId(location: LocationConfig, azureGroupId: string | null | undefined) {
  return Boolean(azureGroupId) && location.azure_group_id === azureGroupId;
}

function findLocationByAzureGroupId(locations: LocationConfig[], azureGroupId: string | null | undefined) {
  if (!azureGroupId) return null;

  return locations.find((location) => location.azure_group_id === azureGroupId) ?? null;
}

function hasSameLocationIdentity(a: LocationConfig | null, b: LocationConfig | null) {
  return (
    (a?.azure_group_id ?? null) === (b?.azure_group_id ?? null) && (a?.name ?? null) === (b?.name ?? null)
  );
}

function resolveLocationSelection(params: {
  locations: LocationConfig[];
  currentLocation: LocationConfig | null;
  storedLocationId: string | null;
}): LocationSelection {
  const { locations, currentLocation, storedLocationId } = params;

  if (locations.length === 0) {
    return {
      location: null,
      reason: "no_allowed_locations",
    };
  }

  // 1) Keep the current in-memory selection if it is still allowed.
  const currentAllowedLocation = currentLocation
    ? (locations.find((location) => locationMatchesId(location, currentLocation.azure_group_id)) ?? null)
    : null;

  if (currentAllowedLocation) {
    return {
      location: currentAllowedLocation,
      reason: "current_state",
    };
  }

  // 2) Restore the user's saved selected location if it is still allowed.
  const storedAllowedLocation = findLocationByAzureGroupId(locations, storedLocationId);

  if (storedAllowedLocation) {
    return {
      location: storedAllowedLocation,
      reason: "stored_selection",
    };
  }

  // 3) Default to the dynamic/home location when exactly one exists.
  const dynamicLocations = locations.filter((location) => location.type === "dynamic");

  if (dynamicLocations.length === 1) {
    return {
      location: dynamicLocations[0],
      reason: "dynamic_home_location",
    };
  }

  // 4) If the user only has one allowed location total, use it.
  if (locations.length === 1) {
    return {
      location: locations[0],
      reason: "single_allowed_location",
    };
  }

  // 5) Do not guess when there are multiple possible home/default locations.
  if (dynamicLocations.length > 1) {
    return {
      location: null,
      reason: "multiple_dynamic_locations",
    };
  }

  return {
    location: null,
    reason: "multiple_allowed_locations",
  };
}

async function fetchCurrentAuthorizationForUser(params: {
  userEmail: string;
  tokenGroups: string[];
}): Promise<CurrentAuthorizationResult> {
  const { userEmail, tokenGroups } = params;

  const requestId = makeRequestId();
  const startedAt = performance.now();
  const cacheKey = userEmail.toLowerCase();

  const existingRequest = inFlightByUser.get(cacheKey);

  if (existingRequest) {
    if (shouldLogAuthDebug()) {
      console.info("useAuthorization: current authorization request already in flight for user", {
        requestId,
        userEmail,
      });
    }

    return existingRequest;
  }

  const requestPromise = (async () => {
    try {
      const sortedTokenGroups = tokenGroups.slice().sort();

      if (shouldLogAuthDebug()) {
        console.info("useAuthorization: fetching current authorization", {
          requestId,
          userEmail,
          tokenGroupCount: sortedTokenGroups.length,
          tokenGroups: sortedTokenGroups,
          endpoint: "/api/admin/locations/by-groups",
        });
      }

      const res = await fetch("/api/admin/locations/by-groups", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-io-request-id": requestId,
        },
        cache: "no-store",
        body: JSON.stringify({
          // Debug only. The route uses current Graph groups server-side.
          groups: tokenGroups,
        }),
      });

      const durationMs = Math.round(performance.now() - startedAt);

      if (!res.ok) {
        let responseBody = "";

        try {
          responseBody = await res.text();
        } catch {
          responseBody = "[unable to read response body]";
        }

        console.error("useAuthorization: current authorization request failed", {
          requestId,
          userEmail,
          status: res.status,
          statusText: res.statusText,
          durationMs,
          responseBody,
        });

        throw new Error(
          `Failed to fetch current authorization. Status ${res.status} ${res.statusText}. Request ${requestId}`,
        );
      }

      const payload = (await res.json()) as CurrentAuthorizationResponse | LocationConfig[];

      // Temporary backward compatibility in case the route still returns LocationConfig[].
      // Once the route response is updated everywhere, this fallback can be removed.
      const result: CurrentAuthorizationResult = Array.isArray(payload)
        ? {
            locations: payload,
            isAuthorizedUserManager: tokenGroups.includes(AUTHORIZED_USER_MANAGER_GROUP_ID),
          }
        : {
            locations: Array.isArray(payload.locations) ? payload.locations : [],
            isAuthorizedUserManager: payload.auth?.isAuthorizedUserManager === true,
          };

      if (Array.isArray(payload)) {
        console.warn("useAuthorization: /api/admin/locations/by-groups returned legacy array response", {
          requestId,
          userEmail,
          managerAuthFallback: "tokenGroups",
        });
      }

      if (shouldLogAuthDebug()) {
        console.info("useAuthorization: current authorization request succeeded", {
          requestId,
          userEmail,
          status: res.status,
          durationMs,
          isAuthorizedUserManager: result.isAuthorizedUserManager,
          locationCount: result.locations.length,
          locationNames: result.locations.map((location) => location.name),
          locationGroupIds: result.locations.map((location) => location.azure_group_id),
          locationTypes: result.locations.map((location) => location.type ?? "unknown"),
        });
      }

      return result;
    } catch (error) {
      console.error("useAuthorization: current authorization request error", {
        requestId,
        userEmail,
        error,
      });

      throw error;
    } finally {
      inFlightByUser.delete(cacheKey);
    }
  })();

  inFlightByUser.set(cacheKey, requestPromise);

  return requestPromise;
}

function useAuthorization() {
  const { data: session, status } = useSession();
  const userEmail = session?.user?.email ?? null;
  const isGazelle = isGazelleEmail(userEmail);

  const locationRef = useRef<LocationConfig | null>(null);

  const [authState, setAuthState] = useState<AuthState>({
    isAuthorized: false,
    isCanadian: false,
    isGazelle: false,
    isLoaded: false,
  });

  const [location, setLocationState] = useState<LocationConfig | null>(null);
  const [locationGroups, setLocationGroups] = useState<LocationConfig[]>([]);

  const setTransientLocationState = useCallback((loc: LocationConfig | null) => {
    locationRef.current = loc;
    setLocationState(loc);
  }, []);

  const setLocation = useCallback(
    (loc: LocationConfig | null) => {
      locationRef.current = loc;
      setLocationState(loc);

      if (typeof window !== "undefined" && userEmail) {
        const storageKey = getLocationStorageKey(userEmail);

        if (loc) {
          window.localStorage.setItem(storageKey, loc.azure_group_id);
        } else {
          window.localStorage.removeItem(storageKey);
        }
      }
    },
    [userEmail],
  );

  const refreshLocations = useCallback(async () => {
    if (!userEmail) return [];

    const tokenGroups: string[] = Array.isArray(session?.user.groups) ? session.user.groups : [];

    const result = await fetchCurrentAuthorizationForUser({
      userEmail,
      tokenGroups,
    });

    const storedLocationId = getStoredLocationId(userEmail);
    const selection = resolveLocationSelection({
      locations: result.locations,
      currentLocation: locationRef.current,
      storedLocationId,
    });

    setLocationGroups(result.locations);
    setLocation(selection.location);

    setAuthState((prev) => ({
      ...prev,
      isAuthorized: result.isAuthorizedUserManager,
      isCanadian: result.locations.some((loc) => loc.country === "CA"),
      isGazelle,
      isLoaded: true,
    }));

    return result.locations;
  }, [session, userEmail, setLocation, isGazelle]);

  useEffect(() => {
    if (status === "unauthenticated") {
      setAuthState({
        isAuthorized: false,
        isCanadian: false,
        isGazelle,
        isLoaded: true,
      });

      setLocation(null);
      setLocationGroups([]);
      return;
    }

    if (status !== "authenticated") {
      return;
    }

    if (!userEmail) {
      setAuthState({
        isAuthorized: false,
        isCanadian: false,
        isGazelle,
        isLoaded: true,
      });

      setLocation(null);
      setLocationGroups([]);
      return;
    }

    const tokenGroups: string[] = Array.isArray(session?.user.groups) ? session.user.groups : [];

    if (shouldLogAuthDebug()) {
      console.info("useAuthorization: session loaded", {
        userEmail,
        status,
        tokenGroupCount: tokenGroups.length,
        tokenGroups: tokenGroups.slice().sort(),
        managerGroupId: AUTHORIZED_USER_MANAGER_GROUP_ID,
        isGazelle,
      });
    }

    let cancelled = false;

    (async () => {
      try {
        const result = await fetchCurrentAuthorizationForUser({
          userEmail,
          tokenGroups,
        });

        if (cancelled) return;

        const locations = result.locations;
        const canadian = locations.some((loc) => loc.country === "CA");
        const storedLocationId = getStoredLocationId(userEmail);

        const selection = resolveLocationSelection({
          locations,
          currentLocation: locationRef.current,
          storedLocationId,
        });

        setLocationGroups(locations);

        if (!hasSameLocationIdentity(locationRef.current, selection.location)) {
          setLocation(selection.location);
        }

        if (shouldLogAuthDebug()) {
          console.info("useAuthorization: resolved active authorization/location", {
            userEmail,
            isAuthorizedUserManager: result.isAuthorizedUserManager,
            reason: selection.reason,
            currentLocation: locationRef.current
              ? {
                  name: locationRef.current.name,
                  azure_group_id: locationRef.current.azure_group_id,
                  type: locationRef.current.type ?? "unknown",
                }
              : null,
            storedLocationId,
            selectedLocation: selection.location
              ? {
                  name: selection.location.name,
                  azure_group_id: selection.location.azure_group_id,
                  type: selection.location.type ?? "unknown",
                }
              : null,
            allowedLocationCount: locations.length,
            allowedLocations: locations.map((loc) => ({
              name: loc.name,
              azure_group_id: loc.azure_group_id,
              type: loc.type ?? "unknown",
            })),
          });
        }

        setAuthState({
          isAuthorized: result.isAuthorizedUserManager,
          isCanadian: canadian,
          isGazelle,
          isLoaded: true,
        });
      } catch (err) {
        console.error("useAuthorization: error fetching current authorization", err);
        if (cancelled) return;

        setLocationGroups([]);

        // Do not clear localStorage here. A transient lookup failure should not erase
        // the user's saved selected location.
        setTransientLocationState(null);

        // Fail closed for manager/admin authorization when current Graph validation fails.
        setAuthState({
          isAuthorized: false,
          isCanadian: false,
          isGazelle,
          isLoaded: true,
        });
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [session, status, userEmail, setLocation, setTransientLocationState, isGazelle]);

  return {
    ...authState,
    session,
    status,
    location,
    locationGroups,
    setLocation,
    refreshLocations,
  };
}

export default useAuthorization;
